import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import L from 'leaflet';
import { getHullPolygons } from './convexHull';
import { getObstaclesInHull } from '../api/client';
import { formatDMS } from './dms';

const DEFAULT_RADIUS_KM = 15;

/** Great-circle distance in km between two lat/lon points. */
function haversineKm(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const toRad = d => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function csvEscape(v) {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsvRows(rows, filename) {
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const lines = [headers.join(',')];
    rows.forEach(row => lines.push(headers.map(h => csvEscape(row[h])).join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// Dedicated export for the "real surface penetration" report — one row per
// (candidate, surface) pair a page has confirmed genuinely breaches, in the
// exact column set requested for this report (distinct from the general
// exportCsv below, which is one row per candidate with the raw-elevation
// Step1/Step2 comparison). `entries`: [{ r, kind, surfaceLabel, penetrationAmount }, ...]
// — r is the raw SWOT/SOF row, kind is 'SWOT'|'SOF', surfaceLabel/
// penetrationAmount are the specific surface name and its numeric margin.
export function exportSurfacePenetratingCsv(entries, radiusKm) {
    const rows = entries.map(({ r, surfaceLabel, penetrationAmount }) => ({
        CAA_ID: r.caa_id || '',
        Type: r.type || '',
        Site_Name: r.site_name || '',
        Status: r.status || '',
        Latitude_DMS: formatDMS(r.lat, true),
        Longitude_DMS: formatDMS(r.lon, false),
        Site_Elevation_m: r.site_elev_m ?? '',
        Height_m: r.hgt_m ?? '',
        V_Tolerance_m: r.v_tolerance_m ?? '',
        Surface_Penetrated: surfaceLabel,
        Penetration_Amount_m: penetrationAmount,
    }));
    downloadCsvRows(rows, `surface_penetrating_obstacles_${radiusKm}km_${new Date().toISOString().slice(0, 10)}.csv`);
}

// `penetratingObstacleIds` (optional) — ids of the project's own obstacles
// that the calling page has already flagged as actually penetrating some
// surface (each page knows this from its own assessment results — it's
// exactly what already drives that page's red/clear obstacle icon colour).
// Step 1 below (projectMaxTotalElev) compares candidates against the
// tallest point in the WHOLE project, penetrating or not — a shield
// candidate can beat that just by being tall while sitting nowhere near an
// actual violation. Step 2 (projectPenetratingMaxTotalElev) narrows the
// reference to only the obstacles that are actually causing a penetration,
// which is the comparison that's actually meaningful for "does this real
// structure shield our violation" — kept as a second, additional check
// rather than replacing Step 1, since Step 1 is still useful context when a
// project has no penetrations at all yet.
export function useShield(mapInst, obstacles, penetratingObstacleIds) {
    const shieldLayerRef = useRef(null);
    const fetchedRadiusRef = useRef(null); // radius (km) shieldData was fetched for
    const [shieldOn, setShieldOn] = useState(false);
    const [shieldData, setShieldData] = useState(null);
    const [shieldLoading, setShieldLoading] = useState(false);
    const [shieldError, setShieldError] = useState('');
    const [radiusKm, setRadiusKmRaw] = useState(DEFAULT_RADIUS_KM);

    const projectMaxTotalElev = useMemo(() => {
        const totals = (obstacles || [])
            .filter(o => o.elevation_m != null)
            .map(o => (parseFloat(o.elevation_m) || 0) + (parseFloat(o.v_tolerance) || 0));
        return totals.length ? Math.max(...totals) : null;
    }, [obstacles]);

    const penetratingIdSet = useMemo(() => new Set(penetratingObstacleIds || []), [penetratingObstacleIds]);
    // Full records (not just the max) — a shield candidate can sit above
    // some penetrating obstacles and below others, so the popup lists each
    // one it actually beats by name, not just a single aggregate number.
    const penetratingObstacleRecords = useMemo(() => (
        (obstacles || [])
            .filter(o => penetratingIdSet.has(o.id) && o.elevation_m != null)
            .map(o => ({
                id: o.id,
                name: o.name || `Obstacle ${o.id}`,
                totalElev: (parseFloat(o.elevation_m) || 0) + (parseFloat(o.v_tolerance) || 0),
            }))
    ), [obstacles, penetratingIdSet]);
    const projectPenetratingMaxTotalElev = useMemo(() => {
        if (!penetratingObstacleRecords.length) return null;
        return Math.max(...penetratingObstacleRecords.map(o => o.totalElev));
    }, [penetratingObstacleRecords]);

    // Simple centroid of the project's own obstacles — "project centre" for
    // reporting how far away a shielding candidate actually is.
    const projectCentre = useMemo(() => {
        const pts = (obstacles || []).filter(o => o.latitude != null && o.longitude != null);
        if (!pts.length) return null;
        const lat = pts.reduce((s, o) => s + parseFloat(o.latitude), 0) / pts.length;
        const lon = pts.reduce((s, o) => s + parseFloat(o.longitude), 0) / pts.length;
        return { lat, lon };
    }, [obstacles]);

    const fetchShield = useCallback(async (radius) => {
        const hulls = getHullPolygons(obstacles || []);
        if (!hulls.length) {
            setShieldError('Need at least 3 obstacles with coordinates to compute a hull.');
            return;
        }
        setShieldLoading(true);
        setShieldError('');
        try {
            const points = hulls[0].points.map(p => [p.lng, p.lat]);
            const { data } = await getObstaclesInHull(points, radius * 1000);
            setShieldData(data);
            fetchedRadiusRef.current = radius;
        } catch (e) {
            console.error('Failed to load shielding obstacles', e);
            setShieldError(e.response?.data?.detail || e.message);
        } finally {
            setShieldLoading(false);
        }
    }, [obstacles]);

    const toggleShield = useCallback(async () => {
        if (shieldOn) { setShieldOn(false); return; }
        setShieldOn(true);
        if (shieldData && fetchedRadiusRef.current === radiusKm) return; // already fetched for this radius
        await fetchShield(radiusKm);
    }, [shieldOn, shieldData, radiusKm, fetchShield]);

    // User-editable shielding distance — re-fetch immediately if shielding is
    // already on, so changing the number updates the map without a second click.
    const setRadiusKm = useCallback((value) => {
        const n = Math.max(0.1, Math.min(100, parseFloat(value) || DEFAULT_RADIUS_KM));
        setRadiusKmRaw(n);
        if (shieldOn) {
            setShieldData(null);
            fetchShield(n);
        }
    }, [shieldOn, fetchShield]);

    useEffect(() => {
        const map = mapInst.current;
        if (!map) return;
        if (shieldLayerRef.current) { map.removeLayer(shieldLayerRef.current); shieldLayerRef.current = null; }
        if (!shieldOn || !shieldData || projectMaxTotalElev == null) return;

        const group = L.featureGroup(); // featureGroup (not layerGroup) so .getBounds() below works
        const renderSet = (rows, kind) => {
            rows.forEach(r => {
                if (r.lat == null || r.lon == null) return;
                const totalElev = (parseFloat(r.elev_m) || 0) + (parseFloat(r.v_tolerance_m) || 0);
                const shieldsProjectMax = totalElev > projectMaxTotalElev;
                // Which specific penetrating obstacles this candidate actually
                // beats — a candidate can sit above some and below others, so
                // this drives both the popup list and the marker colour, not
                // just whether it beats the single worst one.
                const shieldedObstacles = penetratingObstacleRecords
                    .filter(o => totalElev > o.totalElev)
                    .sort((a, b) => (totalElev - a.totalElev) - (totalElev - b.totalElev)); // tightest margin first
                const shieldsPenetrating = projectPenetratingMaxTotalElev != null && shieldedObstacles.length > 0;
                // Step 2 (against the project's actual penetrating obstacles) is
                // the meaningful check when there's a penetration to shield in
                // the first place; fall back to Step 1 (project max) when there
                // isn't one yet, so shielding still shows *something* useful.
                const shields = projectPenetratingMaxTotalElev != null ? shieldsPenetrating : shieldsProjectMax;
                const color = shields ? '#e84040' : '#8a94a6';
                const marker = L.circleMarker([r.lat, r.lon], {
                    radius: shields ? 7 : 5, color, weight: shields ? 2 : 1,
                    fillColor: color, fillOpacity: shields ? 0.9 : 0.5,
                });
                const label = kind === 'SWOT' ? (r.caa_id || r.type) : (r.site_name || r.type);
                const step1Line = shieldsProjectMax
                    ? `<b style="color:#e84040">Step 1 ✓ SHIELDS project max</b> — ${(totalElev - projectMaxTotalElev).toFixed(1)} m higher than project max`
                    : `<span style="color:#8a94a6">Step 1: Below project max (${(projectMaxTotalElev - totalElev).toFixed(1)} m lower)</span>`;
                const step2Line = projectPenetratingMaxTotalElev == null
                    ? `<br/><span style="color:#8a94a6">Step 2: no penetrating obstacles in this project yet — nothing to shield against</span>`
                    : shieldedObstacles.length
                        ? `<br/><b style="color:#FFB020">Step 2 ✓ SHIELDS ${shieldedObstacles.length} penetrating obstacle${shieldedObstacles.length > 1 ? 's' : ''}:</b>` +
                          `<div style="max-height:110px; overflow-y:auto; margin-top:2px; padding-right:4px;">` +
                          shieldedObstacles.map(o => `${o.name} (+${(totalElev - o.totalElev).toFixed(1)} m)<br/>`).join('') +
                          `</div>`
                        : `<br/><span style="color:#8a94a6">Step 2: Below the project's worst penetrating point (${(projectPenetratingMaxTotalElev - totalElev).toFixed(1)} m lower)</span>`;
                const distLine = projectCentre
                    ? `Distance from project centre: ${haversineKm(projectCentre.lat, projectCentre.lon, r.lat, r.lon).toFixed(2)} km<br/>`
                    : '';
                const basePopupHtml =
                    `<b>${kind}: ${label}</b><br/>ID: ${r.caa_id || '—'} (row ${r.ogc_fid})<br/>Type: ${r.type || '—'}<br/>` +
                    `Elev: ${r.elev_m ?? '—'} m + V-Tol ${r.v_tolerance_m ?? 0} m = ${totalElev.toFixed(1)} m<br/>` +
                    `${distLine}${step1Line}${step2Line}`;
                marker.bindPopup(basePopupHtml);
                // Tagged so a page can enrich this popup with a real
                // per-surface verdict (e.g. OLS's departure surface) in a
                // follow-up effect, without this generic hook needing to
                // know about any specific surface — see OLSPage.jsx.
                // Must match the `shield_${kind}_${ogc_fid}` pseudo-obstacle
                // id every page's real-surface shield check sends to the
                // backend (see e.g. OLSPage.jsx's pseudoObstacles) — this key
                // is how each page's popup-enrichment effect looks its own
                // per-candidate verdict back up against this exact marker.
                marker._shieldKey = `shield_${kind}_${r.ogc_fid}`;
                marker._basePopupHtml = basePopupHtml;
                // Lets a page's own real-surface enrichment effect decide
                // whether to override this marker's colour (e.g. yellow for
                // "penetrates the real surface but isn't raw-elevation-tall
                // enough to shield") without duplicating the elevation math
                // that produced this base red/grey verdict.
                marker._shieldsRaw = shields;
                group.addLayer(marker);
            });
        };
        renderSet(shieldData.swot || [], 'SWOT');
        renderSet(shieldData.sof || [], 'SOF');

        shieldLayerRef.current = group;
        group.addTo(map);
        if (group.getLayers().length > 0) {
            map.fitBounds(map.getBounds().extend(group.getBounds()).pad(0.05));
        }
    }, [shieldOn, shieldData, projectMaxTotalElev, projectPenetratingMaxTotalElev, projectCentre, mapInst]);

    // opts.filterFn(r, kind) -> keep this row? (e.g. only candidates a page
    // has confirmed genuinely penetrate a real surface — that check lives in
    // the calling page, not here, since it depends on that page's own
    // per-surface assessment, computed after shieldData already exists).
    // opts.enrichFn(r, kind) -> extra { ColumnName: value } fields to merge
    // in, e.g. naming exactly which real surface(s) a row penetrates.
    // opts.suffix -> filename suffix so a filtered export doesn't overwrite
    // the full one.
    const exportCsv = useCallback((opts = {}) => {
        const { filterFn, enrichFn, suffix = '' } = opts;
        if (!shieldData) return;
        const rows = [];
        const push = (list, kind) => {
            (list || []).forEach(r => {
                if (r.lat == null || r.lon == null) return;
                if (filterFn && !filterFn(r, kind)) return;
                const elev = parseFloat(r.elev_m) || 0;
                const vtol = parseFloat(r.v_tolerance_m) || 0;
                const totalElev = elev + vtol;
                const heightAboveProject = projectMaxTotalElev != null ? totalElev - projectMaxTotalElev : null;
                const heightAbovePenetrating = projectPenetratingMaxTotalElev != null ? totalElev - projectPenetratingMaxTotalElev : null;
                const label = kind === 'SWOT' ? (r.caa_id || r.type || '') : (r.site_name || r.type || '');
                const distKm = projectCentre ? haversineKm(projectCentre.lat, projectCentre.lon, r.lat, r.lon) : null;
                rows.push({
                    Type: kind,
                    Label: label,
                    CAA_ID: r.caa_id || '',
                    Row_ID: r.ogc_fid ?? '',
                    Lat_DMS: formatDMS(r.lat, true),
                    Lon_DMS: formatDMS(r.lon, false),
                    Distance_From_Project_Centre_km: distKm == null ? '' : distKm.toFixed(2),
                    Elevation_m: elev,
                    V_Tolerance_m: vtol,
                    Total_Elevation_m: totalElev.toFixed(2),
                    Height_Above_Highest_Application_Obstacle_m: heightAboveProject == null ? '' : heightAboveProject.toFixed(2),
                    Step1_Shields_Project_Max: heightAboveProject != null && heightAboveProject > 0 ? 'YES' : 'NO',
                    Height_Above_Worst_Penetrating_Obstacle_m: heightAbovePenetrating == null ? '' : heightAbovePenetrating.toFixed(2),
                    Step2_Shields_Penetrating_Obstacle: heightAbovePenetrating == null ? 'N/A' : (heightAbovePenetrating > 0 ? 'YES' : 'NO'),
                    ...(enrichFn ? enrichFn(r, kind) : {}),
                });
            });
        };
        push(shieldData.swot, 'SWOT');
        push(shieldData.sof, 'SOF');
        if (!rows.length) return;

        const headers = Object.keys(rows[0]);
        const csvLines = [headers.join(',')];
        rows.forEach(row => {
            csvLines.push(headers.map(h => {
                const v = row[h] == null ? '' : String(row[h]);
                return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
            }).join(','));
        });
        const csv = csvLines.join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `shielding_obstacles${suffix}_${radiusKm}km_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [shieldData, projectMaxTotalElev, projectPenetratingMaxTotalElev, projectCentre, radiusKm]);

    return {
        shieldOn, toggleShield, shieldData, shieldLoading, shieldError, projectMaxTotalElev,
        projectPenetratingMaxTotalElev,
        radiusKm, setRadiusKm, exportCsv, shieldLayerRef,
    };
}
